import React, {useEffect, useRef, useState} from 'react'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    IconButton,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay
} from '@chakra-ui/react'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Heading,
    SimpleGrid,
    Skeleton,
    Stack,
    Text,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import FormActionButtons from '../../../../components/forms/form-action-button/index'
import {useForm} from 'react-hook-form'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'

import LoadingSpinner from '../../../../components/loading-spinner/index'
import {PlusIcon} from '@salesforce/retail-react-app/app/components/icons'
import ActionCard from '../../../../components/action-card/index'
import AddressFields from '../../../../components/forms/address-fields/index'
import AddressDisplay from '../../../../components/address-display/index'
import PageActionPlaceHolder from '../../../../components/page-action-placeholder/index'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {nanoid} from 'nanoid'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {addAddressCardStyle, addressHeadingStyle, editAddressHeadingStyle} from './index.style'
import {pxToRem} from '../../../../utills/pxToRem'
import {modalContentStyle} from './index.style'
import {closeButton} from '../../../../components/addToCartModal/index.style'
import colors from '../../../../theme/foundations/colors'
import upsAddressValidation from '../../../../hooks/ups-address-validation'
import AddressValidationModal from '../../../../components/address-validation-modal'

const DEFAULT_SKELETON_COUNT = 3

const ShippingAddressForm = ({
    form,
    hasAddresses,
    selectedAddressId,
    toggleEdit,
    submitForm,
    isModalOpen,
    handleModalClose
}) => {
    const modalRef = useRef()
    useEffect(() => {
        if (isModalOpen) {
            setTimeout(() => {
                modalRef.current.scrollTo({top: 0, behavior: 'auto'})
            }, 2000) // small delay ensures modal doesn't override it
        }
    }, [isModalOpen])
    return (
        <Modal ref={modalRef} isOpen={isModalOpen} onClose={toggleEdit} preserveScrollBarGap={true}>
            <ModalOverlay background="blackAlpha.630" />
            <ModalContent sx={modalContentStyle}>
                <Box
                    {...(hasAddresses && {
                        gridColumn: [1, 'span 2', 'span 2', 'span 2', 'span 3']
                    })}
                >
                    {form.formState.isSubmitting && <LoadingSpinner />}

                    <Heading sx={editAddressHeadingStyle}>
                        {selectedAddressId ? (
                            <FormattedMessage
                                defaultMessage="Edit Address"
                                id="shipping_address_form.heading.edit_address"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Add New Address"
                                id="shipping_address_form.heading.new_address"
                            />
                        )}
                    </Heading>

                    <IconButton
                        icon={
                            <img
                                src={getAssetUrl('static/img/pdp/close.svg')}
                                height="15"
                                width="15"
                                alt="Cavender's"
                            />
                        }
                        variant="unstyled"
                        onClick={toggleEdit}
                        sx={closeButton}
                    />

                    <ModalBody padding="0px">
                        <Box>
                            <form onSubmit={form.handleSubmit(submitForm)}>
                                <Stack spacing={6}>
                                    {form.formState.errors?.global && (
                                        <Alert status="error">
                                            <AlertIcon color="red.600" boxSize={4} />
                                            <Text fontSize="sm" ml={3}>
                                                {form.formState.errors.global.message}
                                            </Text>
                                        </Alert>
                                    )}
                                    <AddressFields form={form} isAccountAddress={true} />
                                    <FormActionButtons
                                        directionType={'row'}
                                        onCancel={toggleEdit}
                                        isCancle={true}
                                    />
                                </Stack>
                            </form>
                        </Box>
                    </ModalBody>
                </Box>
            </ModalContent>
        </Modal>
    )
}

ShippingAddressForm.propTypes = {
    form: PropTypes.object,
    hasAddresses: PropTypes.bool,
    selectedAddressId: PropTypes.string,
    toggleEdit: PropTypes.func,
    submitForm: PropTypes.func,
    isModalOpen: PropTypes.bool,
    handleModalClose: PropTypes.bool
}

const successfullyAddedAddress = defineMessage({
    defaultMessage: 'New address saved',
    id: 'account_addresses.info.new_address_saved'
})

const successfullyUpdatedAddress = defineMessage({
    defaultMessage: 'Address updated',
    id: 'account_addresses.info.address_updated'
})

const successfullyRemovedAddress = defineMessage({
    defaultMessage: 'Address removed',
    id: 'account_addresses.info.address_removed'
})
const AccountAddresses = () => {
    const {formatMessage} = useIntl()
    const {data: customer, isLoading, refetch} = useCurrentCustomer()
    const {isRegistered, addresses, customerId} = customer

    const addCustomerAddress = useShopperCustomersMutation('createCustomerAddress')
    const updateSavedAddress = useShopperCustomersMutation('updateCustomerAddress')
    const removeCustomerAddress = useShopperCustomersMutation('removeCustomerAddress')

    const [isEditing, setIsEditing] = useState(false)
    const [selectedAddressId, setSelectedAddressId] = useState(false)
    const showToast = useToast()
    const form = useForm()

    const headingRef = useRef()
    const [isEditingAddress, setIsEditingAddress] = useState(false)
    const [enableValidateAddress, setEnableValidateAddress] = useState(false)
    const [enteredAddress, setEnteredAddress] = useState({})

    const {
        data: validatedAddress,
        refetch: refetchCustomerSearch,
        dataUpdatedAt
    } = upsAddressValidation(enteredAddress, enableValidateAddress)

    const {
        isOpen: isOpenUPSValidation,
        onOpen: onOpenUPSValidation,
        onClose: onCloseUPSValidation
    } = useDisclosure()

    const handleValidateAddress = (address) => {
        const {address1, city, stateCode, postalCode, countryCode} = address
        setEnteredAddress({
            address1,
            city,
            stateCode,
            postalCode,
            countryCode: (countryCode || 'US').toUpperCase()
        })
        setEnableValidateAddress(true)
        setIsModalOpen(false)
    }
    const handleConfirm = (address) => {
        if (Array.isArray(address.address1)) {
            address.address1 = address.address1.join(' ')
        }
        onCloseUPSValidation()
        submitForm(address)
        setEnableValidateAddress(false)
    }

    const handleCancel = () => {
        onCloseUPSValidation()
        setEnableValidateAddress(false)
        setIsModalOpen(true)
    }

    useEffect(() => {
        if (enableValidateAddress && enteredAddress?.address1) {
            refetchCustomerSearch()
        }
    }, [enteredAddress, enableValidateAddress])

    useEffect(() => {
        if (!customer?.addresses && !isEditingAddress) {
            setIsEditingAddress(true)
        }
    }, [customer])

    useEffect(() => {
        if (!enableValidateAddress) return
        const XAVResponse = validatedAddress?.UPSResponse?.responseObject?.XAVResponse || {}
        if (Object.prototype.hasOwnProperty.call(XAVResponse, 'NoCandidatesIndicator')) {
            onOpenUPSValidation(true)
        } else if (Object.prototype.hasOwnProperty.call(XAVResponse, 'AmbiguousAddressIndicator')) {
            onOpenUPSValidation(true)
        } else {
            submitForm(form.getValues())
            setEnableValidateAddress(false)
        }
    }, [validatedAddress, dataUpdatedAt])

    useEffect(() => {
        // Focus the 'Addresses' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    // keep track of the edit buttons so we can focus on them later for accessibility
    const [editBtnRefs, setEditBtnRefs] = useState({})
    const [isModalOpen, setIsModalOpen] = useState(false)
    useEffect(() => {
        const currentRefs = {}
        addresses?.forEach(({addressId}) => {
            currentRefs[addressId] = React.createRef()
        })
        setEditBtnRefs(currentRefs)
    }, [addresses])

    const hasAddresses = addresses?.length > 0
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }
    const submitForm = async (address) => {
        try {
            let data
            form.clearErrors()
            if (selectedAddressId) {
                const body = {
                    ...address,
                    addressId: selectedAddressId
                }
                data = await updateSavedAddress.mutateAsync({
                    body,
                    parameters: {
                        customerId,
                        addressName: selectedAddressId
                    }
                })
            } else {
                const body = {
                    ...address,
                    addressId: nanoid()
                }
                data = await addCustomerAddress.mutateAsync({
                    body,
                    parameters: {customerId: customer.customerId}
                })
            }
            if (data) {
                toggleEdit()
                showToast({
                    title: selectedAddressId
                        ? formatMessage(successfullyUpdatedAddress)
                        : formatMessage(successfullyAddedAddress),
                    status: 'success',
                    isClosable: true
                })
                refetch()
            }
        } catch (error) {
            form.setError('global', {type: 'manual', message: error.message})
        }
    }

    const removeAddress = async (addressId) => {
        try {
            if (addressId === selectedAddressId) {
                setSelectedAddressId(undefined)
                setIsEditing(false)
                form.reset({addressId: ''})
            }
            await removeCustomerAddress.mutateAsync(
                {
                    parameters: {
                        customerId,
                        addressName: addressId
                    }
                },
                {
                    onSuccess: () => {
                        showToast({
                            title: formatMessage(successfullyRemovedAddress),
                            status: 'success',
                            isClosable: true
                        })
                        // Move focus to header after we successfully remove address
                        headingRef?.current?.focus()
                    }
                }
            )
        } catch (error) {
            showError()
            throw error
        }
    }

    const handleModalClose = () => {
        setIsModalOpen(false)
    }

    const toggleEdit = (address) => {
        form.reset({...address})

        setIsModalOpen(true)
        window.scrollTo(0, 0)

        if (address?.addressId) {
            setSelectedAddressId(address.addressId)
            setIsEditing(true)
        } else {
            // Focus on the edit button that opened the form when the form closes
            // otherwise focus on the heading if we can't find the button
            const focusAfterClose = editBtnRefs[selectedAddressId]?.current ?? headingRef?.current
            focusAfterClose?.focus()
            setSelectedAddressId(undefined)
            setIsEditing(!isEditing)
        }
    }

    return (
        <Stack data-testid="account-addresses-page" marginTop={['0px', '0px', '0px', pxToRem(64)]}>
            <Heading sx={addressHeadingStyle} tabIndex="0" ref={headingRef}>
                <FormattedMessage
                    defaultMessage="Addresses"
                    id="account_addresses.title.addresses"
                />
            </Heading>

            {isLoading && (
                <SimpleGrid
                    paddingRight={[pxToRem(11), pxToRem(11), pxToRem(11), pxToRem(30)]}
                    marginTop={`${pxToRem(25)} !important`}
                    pb={[pxToRem(30), pxToRem(30), pxToRem(40), pxToRem(40)]}
                    pl={[pxToRem(11), pxToRem(11), pxToRem(11), '0px']}
                    columns={[1, 2, 2, 2, 3]}
                    gap={[pxToRem(15), pxToRem(15), pxToRem(25), pxToRem(25)]}
                >
                    {new Array(DEFAULT_SKELETON_COUNT).fill().map((_, index) => {
                        return (
                            <ActionCard key={index}>
                                <Stack>
                                    <Skeleton height="23px" width="120px" />

                                    <Skeleton height="23px" width="84px" />

                                    <Skeleton height="23px" width="104px" />
                                </Stack>
                            </ActionCard>
                        )
                    })}
                </SimpleGrid>
            )}

            {hasAddresses && (
                <SimpleGrid
                    paddingRight={[pxToRem(11), pxToRem(11), pxToRem(11), pxToRem(30)]}
                    marginTop={[
                        `${pxToRem(15)} !important`,
                        `${pxToRem(15)} !important`,
                        `${pxToRem(25)} !important`,
                        `${pxToRem(25)} !important`
                    ]}
                    pb={[pxToRem(30), pxToRem(30), pxToRem(40), pxToRem(40)]}
                    pl={[pxToRem(11), pxToRem(11), pxToRem(11), '0px']}
                    columns={[1, 2, 2, 2, 3]}
                    gap={[pxToRem(15), pxToRem(15), pxToRem(25), pxToRem(25)]}
                    gridAutoFlow="row dense"
                >
                    {isEditing && !selectedAddressId && (
                        <>
                            <ShippingAddressForm
                                form={form}
                                hasAddresses={hasAddresses}
                                submitForm={handleValidateAddress}
                                selectedAddressId={selectedAddressId}
                                toggleEdit={toggleEdit}
                                isModalOpen={isModalOpen}
                                handleModalClose={handleModalClose}
                            />
                        </>
                    )}

                    {addresses.map((address) => {
                        const editLabel = formatMessage(
                            {
                                defaultMessage: 'Edit {address}',
                                id: 'shipping_address.label.edit_button'
                            },
                            {address: address.address1}
                        )

                        const removeLabel = formatMessage(
                            {
                                defaultMessage: 'Remove {address}',
                                id: 'shipping_address.label.remove_button'
                            },
                            {address: address.address1}
                        )

                        return (
                            <React.Fragment key={address.addressId}>
                                <ActionCard
                                    boxShadow={`${pxToRem(2)} ${pxToRem(4)} ${pxToRem(
                                        16.6
                                    )} ${pxToRem(2)} ${colors.blackAlpha[400]}`}
                                    key={address.addressId}
                                    editBtnRef={editBtnRefs[address.addressId]}
                                    onRemove={() => removeAddress(address.addressId)}
                                    onEdit={() => toggleEdit(address)}
                                    editBtnLabel={editLabel}
                                    removeBtnLabel={removeLabel}
                                    showDivider={true}
                                    position="relative"
                                >
                                    <AddressDisplay isCountryAdded={true} address={address} />
                                </ActionCard>

                                {isEditing && address.addressId === selectedAddressId && (
                                    <ShippingAddressForm
                                        form={form}
                                        hasAddresses={hasAddresses}
                                        submitForm={handleValidateAddress}
                                        selectedAddressId={selectedAddressId}
                                        toggleEdit={toggleEdit}
                                        isModalOpen={isModalOpen}
                                        handleModalClose={handleModalClose}
                                    />
                                )}
                            </React.Fragment>
                        )
                    })}
                    {
                        <Button
                            sx={addAddressCardStyle}
                            variant="outline"
                            height={{lg: 'full'}}
                            minHeight={pxToRem(170)}
                            rounded="base"
                            leftIcon={<PlusIcon display="block" />}
                            onClick={() => toggleEdit()}
                        >
                            <FormattedMessage
                                defaultMessage="Add Address"
                                id="account_addresses.button.add_address"
                            />
                        </Button>
                    }
                </SimpleGrid>
            )}

            {!hasAddresses && !isLoading && (
                <>
                    {!isEditing && isRegistered && (
                        <PageActionPlaceHolder
                            heading={formatMessage({
                                defaultMessage: 'No Saved Addresses',
                                id: 'account_addresses.page_action_placeholder.heading.no_saved_addresses'
                            })}
                            text={formatMessage({
                                defaultMessage: `It doesn't look like you've added any addresses yet. Add an address for a faster checkout experience.`,
                                id: 'account_addresses.page_action_placeholder.message.add_new_address_updated'
                            })}
                            buttonText={formatMessage({
                                defaultMessage: 'Add Address',
                                id: 'account_addresses.page_action_placeholder.button.add_address'
                            })}
                            onButtonClick={() => toggleEdit()}
                        />
                    )}
                    {isEditing && !selectedAddressId && (
                        <ShippingAddressForm
                            form={form}
                            hasAddresses={hasAddresses}
                            submitForm={handleValidateAddress}
                            selectedAddressId={selectedAddressId}
                            toggleEdit={toggleEdit}
                            isModalOpen={isModalOpen}
                            handleModalClose={handleModalClose}
                        />
                    )}
                </>
            )}
            {/* Validate address modal */}
            <AddressValidationModal
                address={form.getValues()}
                isOpen={isOpenUPSValidation}
                onClose={handleCancel}
                hanldeConfiram={handleConfirm}
                hanldeCancel={handleCancel}
                Candidate={validatedAddress?.UPSResponse?.responseObject?.XAVResponse?.Candidate}
                // eslint-disable-next-line no-prototype-builtins
                NoCandidatesIndicator={validatedAddress?.UPSResponse?.responseObject?.XAVResponse?.hasOwnProperty(
                    'AmbiguousAddressIndicator'
                )}
            />
        </Stack>
    )
}

AccountAddresses.getTemplateName = () => 'account-addresses'

export default AccountAddresses
